import React, { useState, useEffect, useCallback } from "react";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import PagesFiltersToolsBar from "app/pages/components/PagesFiltersToolsBar";
import ProcessList from "app/pages/components/ProcessList/ProcessList";
import EmptyListWarning from "app/pages/components/EmptyListWarning/EmptyListWarning";
import PaginationComponent from "app/pages/components/Pagination";
import Accordion, { accordionClasses } from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails, { accordionDetailsClasses } from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import API from "app/services/api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { mapApiError } from "app/utils/errorMessages";

const GRID_ITEM_STYLE = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
};

const ClientesProcessos = () => {
  const { authUser } = useJumboAuth();
  const [count, setCount] = useState(100);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(false);

  const [filters, setFilters] = useState({
    envolvido: "",
    marca: "",
    titular: "",
    num_processo: "",
  });

  const [processos, setProcessos] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleExpansion = () => setExpanded((prev) => !prev);

  const handleChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleCleanFilters = () => {
    setFilters({ envolvido: "", marca: "", titular: "", num_processo: "" });
    setSearchTerm("");
  };

  const fetchProcessos = useCallback((currentPage = page) => {
    if (!authUser?.id) return;

    let url = `/processos-revistas-rpi?usuario_id=${authUser.id}`;
    url += `&page=${currentPage}&perPage=${perPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

    if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    if (filters.envolvido) url += `&envolvido=${encodeURIComponent(filters.envolvido)}`;
    if (filters.marca) url += `&marca=${encodeURIComponent(filters.marca)}`;
    if (filters.titular) url += `&titular=${encodeURIComponent(filters.titular)}`;
    if (filters.num_processo) url += `&num_processo=${encodeURIComponent(filters.num_processo)}`;

    setLoading(true);
    API.get(url)
      .then((response) => {
        const data = response.data;
        setProcessos(data.processos || []);
        setCount(data.total || 0);
        setErrorMsg("");
      })
      .catch((err) => {
        setProcessos([]);
        setCount(0);
        setErrorMsg(mapApiError(err));
      })
      .finally(() => setLoading(false));
  }, [authUser?.id, page, perPage, sortBy, sortOrder, searchTerm, filters]);

  useEffect(() => {
    if (!hasSearched) return;
    fetchProcessos();
  }, [page, perPage, sortBy, sortOrder]);

  useEffect(() => {
    setPage(0);
  }, [sortBy, sortOrder]);

  const handleSearch = () => {
    setHasSearched(true);
    if (page !== 0) setPage(0);
    else fetchProcessos(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <JumboContentLayout header={<h1>Área do Cliente - Meus processos</h1>}>
      <PagesFiltersToolsBar
        value={searchTerm}
        setValue={setSearchTerm}
        onSearch={handleSearch}
      />

      <Accordion
        expanded={expanded}
        onChange={handleExpansion}
        slots={{ transition: Fade }}
        slotProps={{ transition: { timeout: 400 } }}
        style={{ margin: "10px 0" }}
        sx={[
          expanded
            ? {
                [`& .${accordionClasses.region}`]: { height: "auto" },
                [`& .${accordionDetailsClasses.root}`]: { display: "block" },
              }
            : {
                [`& .${accordionClasses.region}`]: { height: 0 },
                [`& .${accordionDetailsClasses.root}`]: { display: "none" },
              },
        ]}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="span">Filtros de busca</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container width="100%" spacing={1} justifyContent="flex-start">
            <Grid item xs={3} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Envolvido:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.envolvido}
                onChange={(e) => handleChangeFilters("envolvido", e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={3} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Marca:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.marca}
                onChange={(e) => handleChangeFilters("marca", e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={3} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Titular:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.titular}
                onChange={(e) => handleChangeFilters("titular", e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={2} style={GRID_ITEM_STYLE}>
              <Typography fontWeight={600}>Nº Processo:</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.num_processo}
                onChange={(e) => handleChangeFilters("num_processo", e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Grid>
          </Grid>

          <Stack direction="row" gap={2} justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              color="info"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              color="info"
              startIcon={<FilterAltOffIcon />}
              onClick={handleCleanFilters}
            >
              Limpar
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMsg}
        </Alert>
      )}

      {!hasSearched ? (
        <Alert severity="info">
          Use os campos acima e clique em <strong>Buscar</strong> para carregar
          os processos.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4, justifyContent: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Aguarde, buscando dados...
          </Typography>
        </Box>
      ) : processos && processos.length > 0 ? (
        <ProcessList processos={processos} />
      ) : (
        <EmptyListWarning />
      )}

      {hasSearched && (
        <PaginationComponent
          count={count}
          page={page}
          perPage={perPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setPage={setPage}
          setPerPage={setPerPage}
          setSortBy={setSortBy}
          setSortOrder={setSortOrder}
        />
      )}
    </JumboContentLayout>
  );
};

export default React.memo(ClientesProcessos);